import axios, { AxiosRequestConfig } from 'axios'
import hash from 'object-hash'

import { commonDispersionChartOptions } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
  magnitudePointFormatter,
  tooltipFormatterScatter,
} from '../utils/compass'
import { Palette, datetimeXAxis } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig, commonChartOptions } from './common'

export namespace Positions {
  export namespace Options {
    type OptionsChartId =
      | 'ticker_put_call_ratio'
      | 'options_open_positions'
      | 'ticker_short_interest'
      | 'ticker_termo_qty'
      | 'ticker_termo_pct_mkt_cap'

    const OPTIONS_CHART_PATHS: Record<OptionsChartId, string> = {
      ticker_put_call_ratio: '/options/ticker-put-call-ratio',
      options_open_positions: '/options/options-open-positions',
      ticker_short_interest: '/options/ticker-short-interest',
      ticker_termo_qty: '/options/ticker-termo-qty',
      ticker_termo_pct_mkt_cap: '/options/ticker-termo-pct-mkt-cap',
    }

    const CONTEXT_ID = 'equity:positions:options'
    const LAST_UPDATE_DATE_PATH =
      '/api/options/positions/options/last-update-date'
    const OPTIONS_CHARTS_PATH = '/api/charts/positions'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: OptionsChartId
      ticker: string
      title?: string
    }

    interface CompassAPIDataBaseWithLastUpdateData extends CompassAPIDataBase {
      lastUpdateDate: string
    }

    interface CompassAPIMultiSeriesChartDataWithLastUpdateData
      extends CompassAPIMultiSeriesChartData {
      lastUpdateDate: string
    }

    interface ScatterData extends CompassAPIDataBaseWithLastUpdateData {
      data: {
        Venci: string
        strike_preco_acao: number
        financeiro: number
        Color: string
      }[]
    }

    export type ChartData =
      | ScatterData
      | CompassAPIMultiSeriesChartDataWithLastUpdateData

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const getDataPromise = (
        chart: ChartConfig['chart'],
        type: 'chart' | 'date'
      ) =>
        axios(
          modifier({
            url:
              type === 'chart'
                ? OPTIONS_CHARTS_PATH + OPTIONS_CHART_PATHS[chart]
                : LAST_UPDATE_DATE_PATH,
            method: 'POST',
            data: { ticker: config.ticker },
          })
        )
          .then((res) => res.data)
          .catch((err) => {
            console.error(err)
            return { data: null }
          })

      return await Promise.all([
        getDataPromise(config.chart, 'chart'),
        getDataPromise(config.chart, 'date'),
      ]).then(([chartData, lastUpdateDateData]) => ({
        ...chartData,
        lastUpdateDate: lastUpdateDateData.last_update_date,
      }))
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ticker_put_call_ratio':
          case 'ticker_short_interest':
          case 'ticker_termo_qty':
          case 'ticker_termo_pct_mkt_cap':
            return getMultipleLineChartProps(Context, {
              ...(config.chart === 'ticker_short_interest' ||
              config.chart === 'ticker_termo_pct_mkt_cap'
                ? { convertToPercentages: Array(data.data.length).fill(true) }
                : {}),
              tooltip: {
                pointFormatter: magnitudePointFormatter({
                  unit:
                    config.chart === 'ticker_short_interest' ||
                    config.chart === 'ticker_termo_pct_mkt_cap'
                      ? '%'
                      : '',
                }),
              },
              chartTitle: config.title,
              chartSubtitle: `(Ref. date: ${data.lastUpdateDate})`,
            })(config, data as CompassAPIMultiSeriesChartData)
          case 'options_open_positions':
            return getChartProps(config, data as ScatterData)
        }
      },
    }

    const getChartProps = (config: ChartConfig, data: ScatterData) => {
      const series = data.data.map((point, idx) => {
        const [year, month, day] = point.Venci.split('/')

        return {
          name: `${
            point.Color === 'red' ? 'Put - ' : 'Call - '
          }${day}/${month}/${year}`,
          animation: false,
          color: point.Color === 'red' ? Palette.orange : Palette.green,
          data: [
            {
              x: new Date(point.Venci.replaceAll('/', '-')).getTime(),
              y: point.strike_preco_acao * 100,
            },
          ],
          id: `series-${idx}`,
          marker: {
            radius: Math.trunc(point.financeiro * 15) + 5,
            symbol: 'circle',
          },
          showInLegend: false,
        }
      })

      const tooltip = {
        backgroundColor: 'rgba(247,247,247,0.85)',
        enabled: true,
        formatter: tooltipFormatterScatter({
          disableXAxisLabel: true,
          yAxisLabel: 'Strike',
          yAxisSuffix: '%',
        }),
        shared: false,
      }

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: `Open position (10% volume) - ${config.ticker}`,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          commonDispersionChartOptions,
          {
            subtitle: {
              style: { fontSize: '16px', color: '#000' },
              text: `(Ref. date: ${data.lastUpdateDate})`,
            },
            series,
            tooltip,
            xAxis: {
              ...datetimeXAxis,
              labels: { format: '{value:%d/%m/%Y}' },
              title: { text: 'Maturity' },
            },
            yAxis: {
              labels: { decimals: 2, format: '{value:.0f}%' },
              title: { text: 'Strike' },
            },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }
  }

  export namespace Overview {
    const CONTEXT_ID = 'equity:positions:overview'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'options_open_positions_ibov'
        | 'put_call_top'
        | 'put_call_bottom'
        | 'b3_termo_pct_mkt_cap_by_ticker'
        | 'b3_termo_pct_mkt_cap'
        | 'b3_termo_qty'
        | 'ibov_short_interest'
        | 'ibov_put_call_ratio'
      context: typeof CONTEXT_ID
    }

    interface OpenPositionIbovScatterData extends CompassAPIDataBase {
      data: {
        maturity: string
        strike: number
        fin: number
        color: string
      }[]
    }

    interface TermoMktCapPoint {
      company_ticker: string
      term_pct_mkt_cap: number
    }

    interface PutCallPoint {
      ticker: string
      put_call_ratio: number
    }

    export interface TermoMktCapChartData {
      data: TermoMktCapPoint[]
    }

    export interface PutCallChartData {
      data: PutCallPoint[]
    }

    export type ChartData =
      | OpenPositionIbovScatterData
      | TermoMktCapChartData
      | PutCallChartData
      | CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'options_open_positions_ibov':
            return getOpenPositionIbovChartProps(
              config,
              data as OpenPositionIbovScatterData
            )
          case 'put_call_top':
          case 'put_call_bottom':
            return getPutCallChartProps(config, data as PutCallChartData)
          case 'b3_termo_pct_mkt_cap_by_ticker':
            return getTermoMktCapChartProps(
              config,
              data as TermoMktCapChartData
            )
          default:
            return getChartProps(config, data as CompassAPIMultiSeriesChartData)
        }
      },
    }

    const getOpenPositionIbovChartProps = (
      config: ChartConfig,
      data: OpenPositionIbovScatterData
    ) => {
      const series = data.data.map((point, idx) => {
        const [year, month, day] = point.maturity.split('-')

        return {
          name: `${
            point.color === 'red' ? 'Put - ' : 'Call - '
          }${day}/${month}/${year}`,
          animation: false,
          color: point.color === 'red' ? Palette.orange : Palette.green,
          data: [
            {
              x: new Date(point.maturity).getTime(),
              y: point.strike / 1000,
            },
          ],
          id: `series-${idx}`,
          marker: {
            radius: Math.trunc(point.fin * 15) + 5,
            symbol: 'circle',
          },
          showInLegend: false,
        }
      })

      const tooltip = {
        backgroundColor: 'rgba(247,247,247,0.85)',
        enabled: true,
        formatter: tooltipFormatterScatter({
          disableXAxisLabel: true,
          yAxisLabel: 'Strike',
          yAxisSuffix: 'k',
        }),
        shared: false,
      }

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          commonDispersionChartOptions,
          {
            series,
            tooltip,
            xAxis: {
              ...datetimeXAxis,
              labels: { format: '{value:%d/%m/%Y}' },
              title: { text: 'Maturity' },
            },
            yAxis: {
              labels: { decimals: 2, format: '{value:.0f}k' },
              title: { text: 'Strike' },
            },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    const getPutCallChartProps = (
      config: ChartConfig,
      data: PutCallChartData
    ) => {
      const series = [
        {
          data: data.data.map((point) => [point.put_call_ratio]),
          id: `series-0`,
          marker: { enabled: false },
          name: 'Put/Call ratio',
          tooltip: { valueDecimals: 2 },
          type: 'column',
          yAxis: 'default',
        },
      ]

      const yAxis = [
        {
          ...(config.chart === 'put_call_top' ? { max: 50, min: 0 } : {}),
          id: 'default',
          opposite: false,
          title: { text: '' },
        },
      ]
      const xAxis = [
        {
          type: 'category',
          categories: data.data.map(({ ticker }) => ticker),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: {
          ...commonChartOptions,
          tooltip: { shared: true, split: false },
          series,
          xAxis,
          yAxis,
        },
        useRegularChart: true,
        stockTools: true,
      }
    }

    const getTermoMktCapChartProps = (
      config: ChartConfig,
      data: TermoMktCapChartData
    ) => {
      const series = [
        {
          data: data.data.map((point) => [point.term_pct_mkt_cap * 100]),
          id: `series-0`,
          marker: { enabled: false },
          name: 'Termo pct. mkt. cap.',
          tooltip: { valueDecimals: 2, valueSuffix: '%' },
          type: 'column',
          yAxis: 'default',
        },
      ]

      const yAxis = [
        {
          id: 'default',
          labels: { format: '{value}%' },
          opposite: false,
          title: { text: '' },
        },
      ]
      const xAxis = [
        {
          type: 'category',
          categories: data.data.map(({ company_ticker }) => company_ticker),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: {
          ...commonChartOptions,
          series,
          xAxis,
          yAxis,
        },
        useRegularChart: true,
        stockTools: true,
      }
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: config.title,
          ...(config.chart === 'ibov_short_interest'
            ? { convertToPercentages: [true, false] }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.chart === 'ibov_short_interest' ? '%' : '',
            }),
          },
          yAxis: [
            { id: 'primary' },
            {
              id: 'secondary',
              opposite: true,
              ...(config.chart === 'ibov_short_interest'
                ? { labelSuffix: '%' }
                : {}),
            },
          ],
        },
        {
          yAxis: ['secondary', 'primary'],
        }
      )(config, data)
  }
}
